/**
 * Страница работы (кампании) на каноне — карта обязательств по статусам,
 * хозяйства по статусу, ход по дням и вода полива (agroai-6al.35).
 *
 * Владелец 04.10: «всё таблицами, неудобно». Первый взгляд теперь — ход работы
 * тремя исходами (`DoneSplit`: выполнено · в процессе · нет отчёта), карта
 * всех контуров, по которым у работы есть обязательство, окрашенная по
 * статусу, и легенда с числами и гектарами. Нажатие на контур открывает общую
 * карточку карты с разделом этой работы (`cardExtra`): статус, даты окна и
 * отчёта, хозяйство со ссылками. Рядом — хозяйства по статусу и график хода.
 *
 * Все числа карты, легенды и списка хозяйств — ОДИН ответ
 * `GET /season/contour-status?campaign_id=` (свёрнутые статусы контуров и
 * число обязательств в каждом статусе). Фильтр `?farm=` (вход из кабинета
 * хозяйства) сужает тот же ответ сервером (`actor_id`), а не прячет строки.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Filter, X } from 'lucide-react';
import { api, type QueryParams, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { AgroMap } from '../../map/MapWidget';
import { Card } from '../../ui/Card';
import { DataTable, type Column } from '../../ui/DataTable';
import { DoneSplit } from '../../ui/DoneSplit';
import { areaShareLabel, areaSplit, haOf as microToHa, haWhole, hasArea, noArea, workHaUnit, type WorkArea } from '../../lib/areaWork';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError, failed } from '../../ui/LoadError';
import { countOf, dateShort, haUnit, num, pct, plural, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import { campaignPace, daysBetween } from '../district/model';
import { PaceChart } from '../district/blocks';
import { Skeleton } from '../../ui/Skeleton';
import { WorkCampaignSkeleton, WorkPaceSkeleton } from './WorkSkeleton';
import {
  STATUS_META,
  STATUS_ORDER,
  contoursOf,
  farmCounts,
  farmRows,
  fromServer,
  layerById,
  statusTotals,
  type ContourStatusRow,
  type FarmStatusRow,
  type WorkContourStatus,
} from './workStatus';
import './work-campaign.css';

type CampaignFarm = Schemas['CampaignFarmOut'];
type Picture = Schemas['SeasonPictureOut'];

const m = defineMessages({
  contourWord: {
    id: 'work.campaign.word.contour',
    defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}',
    description: 'Слово «контур» по числу, без самого числа (стоит рядом с жирным числом в легенде карты)',
  },
  farmWord: {
    id: 'work.campaign.word.farm',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Слово «хозяйство» по числу, без самого числа (знаменатель таблицы хозяйств)',
  },
  fieldWorkWord: {
    id: 'work.campaign.word.fieldWork',
    defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}',
    description: 'Слово «работа на поле» по числу, без самого числа (мелкая подпись рядом с числом)',
  },
  denomArea: {
    id: 'work.campaign.denom.area',
    defaultMessage: '{count, plural, other {га работ}}',
    description: 'Знаменатель полосы выполнения («Знаменатель — 120 га работ»); без числа',
  },
  denomAreaFarm: {
    id: 'work.campaign.denom.areaFarm',
    defaultMessage: '{count, plural, other {га работ хозяйства в этой работе}}',
    description: 'Знаменатель полосы выполнения при выбранном хозяйстве («Знаменатель — 12 га работ хозяйства в этой работе»); без числа',
  },
  denomCount: {
    id: 'work.campaign.denom.count',
    defaultMessage: '{count, plural, one {работа на поле по этой работе плана} few {работы на полях по этой работе плана} many {работ на полях по этой работе плана} other {работы на полях по этой работе плана}}',
    description: 'Знаменатель полосы выполнения, когда счёт по числу работ; без числа',
  },
  denomCountFarm: {
    id: 'work.campaign.denom.countFarm',
    defaultMessage: '{count, plural, one {работа на поле хозяйства в этой работе} few {работы на полях хозяйства в этой работе} many {работ на полях хозяйства в этой работе} other {работы на полях хозяйства в этой работе}}',
    description: 'Знаменатель полосы выполнения при выбранном хозяйстве, счёт по числу работ; без числа',
  },
  farmBadge: {
    id: 'work.campaign.farmBadge',
    defaultMessage: 'Показано только хозяйство <b>{farm}</b>: его контуры и работы на полях в этой работе.',
    description: 'Плашка над страницей работы, когда включён фильтр по хозяйству; <b> — имя хозяйства; {farm} — название хозяйства',
  },
  allFarms: { id: 'work.campaign.allFarms', defaultMessage: 'Все хозяйства работы', description: 'Ссылка в плашке фильтра: снять фильтр и показать все хозяйства работы' },
  farmCabinet: { id: 'work.campaign.farmCabinet', defaultMessage: 'Кабинет хозяйства', description: 'Ссылка в плашке фильтра: открыть кабинет хозяйства' },
  whatStatus: { id: 'work.campaign.what.status', defaultMessage: 'статус работы по контурам', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): статус работы по контурам' },
  emptyTitle: { id: 'work.campaign.empty.title', defaultMessage: 'Работ на полях на контурах нет', description: 'Заголовок пустого состояния страницы работы' },
  emptyFarm: { id: 'work.campaign.empty.farm', defaultMessage: 'У этого хозяйства в работе нет работ на полях в текущем срезе.', description: 'Причина пустого состояния страницы работы, когда выбрано хозяйство' },
  emptyAll: { id: 'work.campaign.empty.all', defaultMessage: 'По этой работе в текущем срезе не назначено ни одной работы на поле.', description: 'Причина пустого состояния страницы работы' },
  colFarm: { id: 'work.campaign.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы хозяйств работы: название хозяйства' },
  onlyFarm: { id: 'work.campaign.onlyFarm', defaultMessage: 'только это хозяйство', description: 'Ссылка в строке хозяйства: сузить страницу до этого хозяйства' },
  colStatus: { id: 'work.campaign.col.status', defaultMessage: 'Статус', description: 'Колонка таблицы хозяйств работы: статус хозяйства' },
  colContours: { id: 'work.campaign.col.contours', defaultMessage: 'Контуры по статусу', description: 'Колонка таблицы хозяйств работы: полоса контуров хозяйства по статусам' },
  colDone: { id: 'work.campaign.col.done', defaultMessage: 'Выполнено', description: 'Колонка таблицы хозяйств работы: доля выполненной площади работ; единица «% площади» в шапке рядом' },
  colUndone: { id: 'work.campaign.col.undone', defaultMessage: 'Не выполнено', description: 'Колонка таблицы хозяйств работы: гектары работ без отчёта «выполнено»; единица «га работ» в шапке рядом' },
  colLand: { id: 'work.campaign.col.land', defaultMessage: 'Земля', description: 'Колонка таблицы хозяйств работы: уникальная земля хозяйства в этой работе; единица «га» в шапке рядом' },
  landNotNamed: { id: 'work.campaign.landNotNamed', defaultMessage: 'не названа', description: 'Ячейка колонки «Земля»: площадь контуров не названа в реестре' },
  wholeFrame: { id: 'work.campaign.wholeFrame', defaultMessage: 'весь кадр', description: 'Кнопка-ссылка в строке хозяйства: вернуть карте весь кадр' },
  onMap: { id: 'work.campaign.onMap', defaultMessage: 'на карте', description: 'Кнопка-ссылка в строке хозяйства: показать на карте контуры этого хозяйства' },
  progressTitle: { id: 'work.campaign.progress.title', defaultMessage: 'Ход работы', description: 'Заголовок карточки «Ход работы» на странице работы' },
  progressInfo: {
    id: 'work.campaign.progress.info',
    defaultMessage:
      'Ход работы — по площади: сумма га выполненных работ ÷ сумма га всех работ (вес работы — площадь самой работы на поле: план — площадь размещения, карантин — земля контура, полив — доля посева); рядом — то же по числу работ. Отчёт принимается фактом без проверки. Нет отчёта после срока — повод уточнить, а не вывод «не сделано».',
    description: 'Подсказка (i) к карточке «Ход работы»',
  },
  progressAside: { id: 'work.campaign.progress.aside', defaultMessage: '{works} на {contours}', description: 'Подпись справа в заголовке «Ход работы»; {works} — «312 работ на полях», {contours} — «120 контуров»' },
  progressAsideArea: { id: 'work.campaign.progress.asideArea', defaultMessage: '{area} · {works} на {contours}', description: 'Подпись справа в заголовке «Ход работы» с площадью; {area} — «1 200 га работ», {works}, {contours} — числа со словами' },
  windowNoteActive: { id: 'work.campaign.progress.windowActive', defaultMessage: 'окно ещё идёт: нет отчёта — значит «ещё не срок», а не невыполнение', description: 'Пояснение под полосой хода работы (по площади), пока окно идёт' },
  windowNoteUpcoming: { id: 'work.campaign.progress.windowUpcoming', defaultMessage: 'работа ещё не началась: отчёту рано появляться', description: 'Пояснение под полосой хода работы (по площади), пока окно впереди' },
  noAreaNote: { id: 'work.campaign.progress.noAreaNote', defaultMessage: 'Площадь не указана — полоса по числу работ.', description: 'Пояснение под полосой хода работы, когда площади нет и полоса считана по числу работ' },
  noAreaNoteActive: { id: 'work.campaign.progress.noAreaNoteActive', defaultMessage: 'Площадь не указана — полоса по числу работ. Окно ещё идёт: нет отчёта — «ещё не срок».', description: 'Пояснение под полосой хода работы, когда площади нет, а окно ещё идёт' },
  factCount: {
    id: 'work.campaign.fact.count',
    defaultMessage: '{done} <small>из {total} выполнено · {pct}</small>',
    description: 'Значение факта «По числу работ»; <small> — мелкий текст; {done}/{total} — числа, {pct} — доля',
  },
  factCountLabel: { id: 'work.campaign.fact.countLabel', defaultMessage: 'По числу работ', description: 'Название факта в карточке «Ход работы»: выполнено по числу работ на полях' },
  factLand: {
    id: 'work.campaign.fact.land',
    defaultMessage: '{reported} <small>из {total} га · каждый гектар один раз</small>',
    description: 'Значение факта «Земля с отчётом»; <small> — мелкий текст; {reported}/{total} — гектары уникальной земли (числа строкой)',
  },
  factLandLabel: { id: 'work.campaign.fact.landLabel', defaultMessage: 'Земля с отчётом', description: 'Название факта в карточке «Ход работы»: уникальная земля, по которой есть отчёт' },
  factOverdueLabel: { id: 'work.campaign.fact.overdueLabel', defaultMessage: 'После срока, нет отчёта', description: 'Название факта в карточке «Ход работы»: окно закрыто, отчёта нет' },
  factOverdueArea: { id: 'work.campaign.fact.overdueArea', defaultMessage: '{ha} <small>{unit} · {works}</small>', description: 'Значение факта «После срока, нет отчёта» с площадью; <small> — мелкий текст; {ha} — число, {unit} — «га работ», {works} — «12 работ на полях»' },
  factCountWord: { id: 'work.campaign.fact.countWord', defaultMessage: '{count} <small>{word}</small>', description: 'Значение факта: число и мелкая подпись («12 работа на поле»); {count} — число строкой, {word} — слово по числу' },
  factCountLand: { id: 'work.campaign.fact.countLand', defaultMessage: '{count} <small>{word} · {ha} га земли</small>', description: 'Значение факта «После срока, нет отчёта» без площади работ, но с землёй; {count} — число, {word} — слово по числу, {ha} — гектары уникальной земли' },
  factWindowLabel: { id: 'work.campaign.fact.windowLabel', defaultMessage: 'Нет отчёта, окно идёт', description: 'Название факта в карточке «Ход работы»: окно идёт, отчёта пока нет' },
  factFarmsLabel: { id: 'work.campaign.fact.farmsLabel', defaultMessage: 'Хозяйств в работе', description: 'Название факта в карточке «Ход работы»: сколько хозяйств участвует' },
  mapTitle: { id: 'work.campaign.map.title', defaultMessage: 'Карта работы: контуры по статусу', description: 'Заголовок карточки с картой работы' },
  mapInfo: {
    id: 'work.campaign.map.info',
    defaultMessage:
      'Карта и легенда — по контурам (полям), а не по гектарам работ: число — контуры статуса, под ним — их земля (каждый гектар один раз) и сколько хозяйств. Нажатие по строке справа оставляет на карте только этот статус. Контур с несколькими работами назван худшим статусом: после срока → окно идёт → впереди → в процессе → выполнено. Хозяйство — худшим статусом своих контуров.',
    description: 'Подсказка (i) к карточке с картой работы',
  },
  showAllStatuses: { id: 'work.campaign.map.showAll', defaultMessage: 'показать все статусы', description: 'Кнопка-ссылка в заголовке карты: снять фильтр по статусам' },
  mapCaptionFarm: { id: 'work.campaign.map.captionFarm', defaultMessage: '{work}: контуры хозяйства {farm}', description: 'Подпись под картой работы при выбранном хозяйстве; {work} — название работы, {farm} — хозяйство' },
  mapCaptionAll: { id: 'work.campaign.map.captionAll', defaultMessage: '{work}: все контуры, где поручена эта работа', description: 'Подпись под картой работы; {work} — название работы' },
  legendAria: { id: 'work.campaign.legend.aria', defaultMessage: 'Статусы работы: контуры и гектары', description: 'aria-label легенды карты работы (список статусов с числами контуров и гектаров)' },
  legendNoArea: { id: 'work.campaign.legend.noArea', defaultMessage: 'площадь не названа', description: 'Мелкая подпись строки легенды: у контуров статуса площадь не названа' },
  legendNoAreaCount: { id: 'work.campaign.legend.noAreaCount', defaultMessage: '{ha} · без площади {count}', description: 'Мелкая подпись строки легенды: земля статуса и сколько контуров без площади; {ha} — «12,5 га», {count} — число' },
  legendFarms: {
    id: 'work.campaign.legend.farms',
    defaultMessage: 'у {count, plural, one {# хозяйства} other {# хозяйств}}',
    description: 'Мелкая подпись строки легенды: у скольких хозяйств есть контуры в этом статусе; {count} — число',
  },
  farmsTitle: { id: 'work.campaign.farms.title', defaultMessage: 'Хозяйства по статусу', description: 'Заголовок карточки с таблицей хозяйств работы' },
  farmsInfo: {
    id: 'work.campaign.farms.info',
    defaultMessage:
      'Статус хозяйства — худший статус его контуров в этой работе; внутри статуса — сначала где больше гектаров работ без «выполнено». «Выполнено, % площади» — доля площади работ хозяйства с отчётом «выполнено»; «Земля» — его контуры в работе, каждый гектар один раз. Имя открывает кабинет хозяйства; «только это хозяйство» сужает эту страницу до его контуров.',
    description: 'Подсказка (i) к карточке «Хозяйства по статусу»',
  },
  chipsAria: { id: 'work.campaign.chips.aria', defaultMessage: 'Статус хозяйства', description: 'aria-label набора кнопок-фильтров по статусу хозяйства' },
  chipAll: { id: 'work.campaign.chips.all', defaultMessage: 'Все', description: 'Кнопка-фильтр «все статусы» над таблицей хозяйств; коротко' },
  farmsEmptyTitle: { id: 'work.campaign.farms.emptyTitle', defaultMessage: 'Хозяйств нет', description: 'Заголовок пустой таблицы хозяйств работы' },
  farmsEmptyReason: { id: 'work.campaign.farms.emptyReason', defaultMessage: 'в этом статусе хозяйств нет', description: 'Причина пустой таблицы хозяйств работы' },
  paceTitle: { id: 'work.campaign.pace.title', defaultMessage: 'Ход работы по дням', description: 'Заголовок карточки (и графика) «Ход работы по дням»' },
  paceWhat: { id: 'work.campaign.pace.what', defaultMessage: 'дневной ряд работы', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): дневной ряд работы' },
  paceNone: { id: 'work.campaign.pace.none', defaultMessage: 'Дневного ряда у сервера нет.', description: 'Сообщение в карточке «Ход работы по дням», когда сервер не дал ряда' },
  farmNo: { id: 'work.campaign.farmNo', defaultMessage: 'Хозяйство №{id}', description: 'Название хозяйства, когда сервер имени не дал; {id} — служебный номер хозяйства' },
  stripTitle: { id: 'work.campaign.strip.title', defaultMessage: '{status}: {count}', description: 'Подсказка полосы контуров хозяйства: статус и число контуров; {status} — подпись статуса' },
  stripCount: { id: 'work.campaign.strip.count', defaultMessage: '{count} {status}', description: 'Мелкая подпись полосы контуров хозяйства: число и короткая подпись статуса, строчными («5 выполнено»)' },
  cardStatus: { id: 'work.campaign.card.status', defaultMessage: 'Статус в работе', description: 'Название строки в карточке контура на карте: статус контура в этой работе' },
  cardNotNamed: { id: 'work.campaign.card.notNamed', defaultMessage: 'не назван', description: 'Значение статуса в карточке контура, когда сервер статус не назвал' },
  cardMulti: {
    id: 'work.campaign.card.multi',
    defaultMessage:
      '{count, plural, one {# работа на поле} few {# работы на полях} many {# работ на полях} other {# работы на полях}}: выполнено {done}, в процессе {progress}, нет отчёта {silent}',
    description: 'Мелкая подпись в карточке контура, когда по контуру несколько работ на полях; {count} — число, остальное — числа строкой',
  },
  cardWindow: { id: 'work.campaign.card.window', defaultMessage: 'Окно работы', description: 'Название строки в карточке контура на карте: окно выполнения работы' },
  cardWindowPlain: { id: 'work.campaign.card.windowPlain', defaultMessage: '{from} – {to}', description: 'Окно работы в карточке контура: даты начала и конца' },
  cardWindowAgo: {
    id: 'work.campaign.card.windowAgo',
    defaultMessage: '{from} – {to} · закрыто {days, plural, one {# день} few {# дня} many {# дней} other {# дня}} назад',
    description: 'Окно работы в карточке контура, окно закрыто; {from}/{to} — даты, {days} — сколько дней назад',
  },
  cardWindowUntil: { id: 'work.campaign.card.windowUntil', defaultMessage: '{from} – {to} · до {date}', description: 'Окно работы в карточке контура, ближайший срок; {date} — дата срока' },
  cardReport: { id: 'work.campaign.card.report', defaultMessage: 'Отчёт', description: 'Название строки в карточке контура на карте: отчёт по контуру' },
  cardReportFailed: { id: 'work.campaign.card.reportFailed', defaultMessage: 'не загрузилось — повторить', description: 'Кнопка-ссылка в карточке контура: отчёт не загрузился, нажать, чтобы повторить' },
  cardReportInProgress: { id: 'work.campaign.card.reportInProgress', defaultMessage: 'в процессе', description: 'Статус отчёта в карточке контура: работа начата, не закончена' },
  cardReportDone: { id: 'work.campaign.card.reportDone', defaultMessage: 'выполнено', description: 'Статус отчёта в карточке контура: работа выполнена' },
  cardReportPlain: { id: 'work.campaign.card.reportPlain', defaultMessage: 'отчёт', description: 'Слово в карточке контура вместо статуса отчёта, когда статус не назван' },
  cardReportLine: { id: 'work.campaign.card.reportLine', defaultMessage: '{progress} · {date}', description: 'Строка отчёта в карточке контура; {progress} — «выполнено», {date} — дата отчёта' },
  cardReportLineBy: { id: 'work.campaign.card.reportLineBy', defaultMessage: '{progress} · {date} · внёс {by}', description: 'Строка отчёта в карточке контура с автором; {by} — кто внёс отчёт (имя из данных)' },
  cardNoReport: { id: 'work.campaign.card.noReport', defaultMessage: 'отчёта нет', description: 'Строка отчёта в карточке контура: отчёта нет' },
  cardFarms: { id: 'work.campaign.card.farms', defaultMessage: 'Хозяйства', description: 'Название строки в карточке контура: несколько хозяйств на контуре' },
  cardFarm: { id: 'work.campaign.card.farm', defaultMessage: 'Хозяйство', description: 'Название строки в карточке контура: хозяйство на контуре' },
  cardThisWork: { id: 'work.campaign.card.thisWork', defaultMessage: 'эта работа у хозяйства', description: 'Ссылка в карточке контура: открыть эту работу, суженную до хозяйства' },
});


export interface WorkCampaignProps {
  campaignId: number;
  /** Параметры среза (`as_of`, `include_demo`). */
  params: QueryParams;
  /** Параметры картины сезона цикла (ось цикла и хозяйства) — для дневного ряда. */
  pictureParams: QueryParams;
  /** Хозяйство из адреса (`?farm=`); `null` — вся работа. */
  farmId: string | null;
  work: { name: string; window: { from: string; to: string }; status: 'done' | 'active' | 'upcoming' };
  asOf: string;
  /** Хозяйства кампании (`/campaigns/{id}/progress`) — для отчётов по контуру. */
  campaignFarms: readonly CampaignFarm[];
  /**
   * Площадь работ кампании (`/campaigns/{id}/progress`.area_work, решение
   * №645): ход работы — по гектарам работ; `null` — площадь не указана.
   */
  campaignArea?: WorkArea | null;
  /** Хвост ссылки со срезом (`?as_of=…`) для карточек хозяйства и контура. */
  linkSearch: string;
  /** Адрес этой работы с фильтром хозяйства (`null` — без фильтра). */
  workHrefFor: (farmId: string | null) => string;
  isFarmer: boolean;
  demo: boolean;
  /** Раздел страницы сразу под ходом работы (вода полива). */
  afterProgress?: ReactNode;
}

export function WorkCampaign(props: WorkCampaignProps) {
  const t = useT();
  const { campaignId, params, farmId, asOf, work } = props;
  const actorId = farmId !== null && Number.isInteger(Number(farmId)) ? Number(farmId) : null;
  const status = useRequest(
    () => api.seasonContourStatus({ ...params, campaign_id: campaignId, ...(actorId !== null ? { actor_id: actorId } : {}) }),
    [JSON.stringify(params), campaignId, actorId],
  );
  const daily = useRequest<Picture>(
    () => api.seasonPicture({ ...props.pictureParams, campaign_id: campaignId, summary: true, daily: true, weeks: 0 }),
    [JSON.stringify(props.pictureParams), campaignId],
  );
  const [only, setOnly] = useState<Set<WorkContourStatus>>(new Set());
  const [farmFilter, setFarmFilter] = useState<WorkContourStatus | 'all'>('all');
  const [focusFarm, setFocusFarm] = useState<number | null>(null);

  const rows = useMemo(() => status.data?.items ?? [], [status.data]);
  const names = useMemo(() => new Map((status.data?.actors ?? []).map(a => [a.id, a.name ?? tr(m.farmNo, { id: String(a.id) })])), [status.data]);
  const totals = useMemo(() => statusTotals(rows), [rows]);
  const farms = useMemo(() => farmRows(rows, names), [rows, names]);
  const byFarmCount = useMemo(() => farmCounts(farms), [farms]);
  /** Хозяйств, у которых есть хотя бы один контур статуса (для легенды). */
  const farmsWith = useMemo(() => Object.fromEntries(STATUS_ORDER.map(s => [s, farms.filter(f => f.byStatus[s] > 0).length])) as Record<WorkContourStatus, number>, [farms]);
  const workStatusById = useMemo(() => layerById(rows), [rows]);
  const visibleIds = useMemo(() => contoursOf(rows, only), [rows, only]);
  const rowById = useMemo(() => new Map(rows.map(r => [String(r.contour_id), r])), [rows]);
  const focusIds = focusFarm !== null ? farms.find(f => f.actorId === focusFarm)?.contourIds ?? visibleIds : visibleIds;
  const farmName = actorId !== null ? names.get(actorId) ?? props.campaignFarms.find(f => f.actor.id === actorId)?.actor.name ?? t(m.farmNo, { id: String(actorId) }) : null;

  const obligations = STATUS_ORDER.reduce((n, s) => n + totals[s].obligations, 0);
  const reported = totals.done.obligations + totals.in_progress.obligations;

  const toggle = (s: WorkContourStatus) =>
    setOnly(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const pace = useMemo(() => {
    const day = daily.data?.summary?.daily ?? null;
    if (!day) return null;
    const total = obligations;
    const daysLeft = work.window.to >= asOf ? daysBetween(asOf, work.window.to) : 0;
    return campaignPace(
      { obligations: { total, reported, verified: 0, done: totals.done.obligations, in_progress: totals.in_progress.obligations }, window_to: work.window.to, days_left: daysLeft },
      day,
      asOf,
    );
  }, [daily.data, obligations, reported, totals, work.window.to, asOf]);

  const farmBadge = farmName ? (
    <div className="wcamp__farm" role="status">
      <Filter size={16} aria-hidden />
      <span>{t(m.farmBadge, { b: c => <strong>{c}</strong>, farm: farmName })}</span>
      {!props.isFarmer ? <Link className="wcamp__farm-clear" to={props.workHrefFor(null)}><X size={14} aria-hidden /> {t(m.allFarms)}</Link> : null}
      <Link to={`/farms/${farmId}${props.linkSearch}`}>{t(m.farmCabinet)} <ArrowUpRight size={14} aria-hidden /></Link>
    </div>
  ) : null;

  if (failed(status)) {
    return <>{farmBadge}<LoadError what={t(m.whatStatus)} error={status.error} onRetry={status.reload} /></>;
  }
  if (!status.data) return <>{farmBadge}<WorkCampaignSkeleton water={!!props.afterProgress} /></>;
  if (!rows.length) {
    return <>{farmBadge}<EmptyState title={t(m.emptyTitle)} reason={farmName ? t(m.emptyFarm) : t(m.emptyAll)} /></>;
  }

  const overdue = totals.overdue.obligations;
  const inWindow = totals.in_window.obligations;
  // Пара «счёт + земля» (07.10.2026): доля обязательств и доля гектаров
  // расходятся, когда контуры разного размера. Гектары — те же, что у сервера
  // на «Обзоре района» (`area` работы в картине сезона, каждый гектар один
  // раз); сумма по контурам — только запасной путь, она расходится с
  // серверной на десятки гектаров (части и перекрытия контуров).
  const serverLand = daily.data?.summary?.works.find(w => w.campaign_id === campaignId)?.area ?? null;
  const haOf = (keys: ReadonlyArray<keyof typeof totals>) => keys.reduce<number | null>((sum, k) => (totals[k].ha === null ? sum : (sum ?? 0) + totals[k].ha!), null);
  const landTotal = serverLand?.total_microha != null ? serverLand.total_microha / 1e6 : haOf(['done', 'in_progress', 'in_window', 'overdue', 'awaiting']);
  const landReported = serverLand?.reported_microha != null ? serverLand.reported_microha / 1e6 : haOf(['done', 'in_progress']) ?? 0;
  const overdueArea = daily.data?.overdue_area?.value;
  const landOverdue = serverLand && overdueArea != null ? overdueArea / 1e6 : totals.overdue.ha;
  const areaSource = actorId !== null ? props.campaignFarms.find(f => f.actor.id === actorId)?.area_work ?? null : props.campaignArea ?? null;
  const area = hasArea(areaSource) && areaSplit(areaSource) ? areaSource : null;

  const farmColumns: Column<FarmStatusRow>[] = [
    {
      key: 'farm',
      header: t(m.colFarm),
      render: f => props.isFarmer
        ? <span>{f.name}</span>
        : <span className="wcamp__farmcell">
            <Link to={`/farms/${f.actorId}${props.linkSearch}`}>{f.name}</Link>
            {actorId === null ? <Link className="astra-link" to={props.workHrefFor(String(f.actorId))}>{t(m.onlyFarm)}</Link> : null}
          </span>,
    },
    {
      key: 'status',
      header: t(m.colStatus),
      render: f => <span className="wcamp__status"><i style={{ background: `var(${STATUS_META[f.status].swatch})` }} aria-hidden />{STATUS_META[f.status].label}</span>,
    },
    {
      key: 'contours',
      header: t(m.colContours),
      render: f => <ContourStrip farm={f} />,
    },
    { key: 'done', header: t(m.colDone), unit: areaShareLabel(), numeric: true, render: f => { const aw = farmArea.get(f.actorId); return aw ? pct(aw.done_pct, 1) : noArea(); } },
    { key: 'undone', header: t(m.colUndone), unit: workHaUnit(), numeric: true, render: f => { const v = undoneHa(f.actorId); return v === null ? noArea() : num(Math.round(v * 10) / 10); } },
    { key: 'ha', header: t(m.colLand), unit: t(UNIT_LABELS.ha), numeric: true, render: f => (f.ha === null ? t(m.landNotNamed) : num(Math.round(f.ha * 10) / 10)) },
    {
      key: 'map',
      header: '',
      render: f => (
        <button type="button" className="astra-link" aria-pressed={focusFarm === f.actorId} onClick={() => setFocusFarm(focusFarm === f.actorId ? null : f.actorId)}>
          {focusFarm === f.actorId ? t(m.wholeFrame) : t(m.onMap)}
        </button>
      ),
    },
  ];
  // Площадь работ по хозяйствам (№645): «выполнено, % площади» и «не
  // выполнено, га»; внутри статуса — сначала где несделанной земли больше.
  const farmArea = new Map(props.campaignFarms.map(f => [f.actor.id, hasArea(f.area_work) ? f.area_work! : null]));
  const undoneHa = (id: number): number | null => {
    const aw = farmArea.get(id);
    return aw ? Math.max(0, (aw.total_microha ?? 0) - (aw.done_microha ?? 0)) / 1e6 : null;
  };
  // Порядок статусов — как у farmRows (худший сверху).
  const statusRank = new Map<WorkContourStatus, number>();
  farms.forEach((f, i) => { if (!statusRank.has(f.status)) statusRank.set(f.status, i); });
  const farmList = (farmFilter === 'all' ? farms : farms.filter(f => f.status === farmFilter))
    .map((f, i) => ({ f, i }))
    .sort((x, y) => (statusRank.get(x.f.status) ?? 0) - (statusRank.get(y.f.status) ?? 0) || (undoneHa(y.f.actorId) ?? -1) - (undoneHa(x.f.actorId) ?? -1) || x.i - y.i)
    .map(x => x.f);

  /**
   * Фильтр статусов рядом с картой — её панель управления: в развёрнутой карте
   * тот же фильтр стоит боковой панелью (журнал №1390).
   */
  const statusFilter = (compact: boolean) => (
  <ul className={compact ? 'wcamp__legend wcamp__legend--compact' : 'wcamp__legend'} aria-label={t(m.legendAria)}>
    {STATUS_ORDER.filter(s => s !== 'awaiting' || totals.awaiting.contours > 0).map(s => {
      const tot = totals[s];
      const on = only.has(s);
      return (
        <li key={s}>
          <button type="button" className="wcamp__legend-row" aria-pressed={on} disabled={tot.contours === 0} onClick={() => toggle(s)}>
            <i className="wcamp__swatch" style={{ background: `var(${STATUS_META[s].swatch})` }} aria-hidden />
            <span className="wcamp__legend-label">
              <strong>{STATUS_META[s].label}</strong>
              <small>{STATUS_META[s].note}</small>
            </span>
            <span className="wcamp__legend-num">
              <strong>{num(tot.contours)}</strong> <small>{plural(tot.contours, m.contourWord)}</small>
              {tot.contours > 0 ? <small>{tot.ha === null ? t(m.legendNoArea) : tot.haMissing ? t(m.legendNoAreaCount, { ha: haUnit(Math.round(tot.ha * 10) / 10), count: num(tot.haMissing) }) : haUnit(Math.round(tot.ha * 10) / 10)}</small> : null}
              {tot.contours > 0 ? <small>{t(m.legendFarms, { count: farmsWith[s] })}</small> : null}
            </span>
          </button>
        </li>
      );
    })}
  </ul>
  );

  return (
    <>
      {farmBadge}

      <div data-guide="progress">
        <Card size="lg" title={t(m.progressTitle)} info={t(m.progressInfo)} aside={area
          ? t(m.progressAsideArea, { area: `${haWhole(microToHa(area.total_microha) ?? 0)} ${workHaUnit()}`, works: countOf(obligations, WORDS.obligation), contours: countOf(rows.length, WORDS.contour) })
          : t(m.progressAside, { works: countOf(obligations, WORDS.obligation), contours: countOf(rows.length, WORDS.contour) })}>
          <div className="wcamp__progress">
            {area ? (
              <DoneSplit
                counts={areaSplit(area)!}
                denominator={farmName ? m.denomAreaFarm : m.denomArea}
                windowNote={work.status === 'active' ? t(m.windowNoteActive) : work.status === 'upcoming' ? t(m.windowNoteUpcoming) : undefined}
              />
            ) : (
              <DoneSplit
                counts={{ total: obligations, reported, inProgress: totals.in_progress.obligations }}
                denominator={farmName ? m.denomCountFarm : m.denomCount}
                windowNote={work.status === 'active' ? t(m.noAreaNoteActive) : t(m.noAreaNote)}
              />
            )}
            <dl className="wcamp__facts">
              <div><dt>{t(m.factCountLabel)}</dt><dd>{t(m.factCount, { small: c => <small>{c}</small>, done: num(totals.done.obligations), total: num(obligations), pct: obligations ? pct((100 * totals.done.obligations) / obligations, 1) : '—' })}</dd></div>
              {landTotal ? <div><dt>{t(m.factLandLabel)}</dt><dd>{t(m.factLand, { small: c => <small>{c}</small>, reported: haWhole(landReported), total: haWhole(landTotal) })}</dd></div> : null}
              <div><dt>{t(m.factOverdueLabel)}</dt><dd>{area
                ? t(m.factOverdueArea, { small: c => <small>{c}</small>, ha: haWhole(microToHa(area.overdue_microha) ?? 0), unit: workHaUnit(), works: countOf(overdue, WORDS.obligation) })
                : landOverdue
                  ? t(m.factCountLand, { small: c => <small>{c}</small>, count: num(overdue), word: plural(overdue, m.fieldWorkWord), ha: haWhole(landOverdue) })
                  : t(m.factCountWord, { small: c => <small>{c}</small>, count: num(overdue), word: plural(overdue, m.fieldWorkWord) })}</dd></div>
              <div><dt>{t(m.factWindowLabel)}</dt><dd>{t(m.factCountWord, { small: c => <small>{c}</small>, count: num(inWindow), word: plural(inWindow, m.fieldWorkWord) })}</dd></div>
              <div><dt>{t(m.factFarmsLabel)}</dt><dd>{num(farms.length)}</dd></div>
            </dl>
          </div>
        </Card>
      </div>

      {props.afterProgress}

      <div data-guide="map">
        <Card size="lg" title={t(m.mapTitle)} info={t(m.mapInfo)} aside={only.size ? <button type="button" className="astra-link" onClick={() => setOnly(new Set())}>{t(m.showAllStatuses)}</button> : countOf(rows.length, WORDS.contour)}>
          <div className="wcamp__mapgrid">
            <AgroMap
              colorBy="workStatus"
              mode="frame"
              height={460}
              legend="collapsed"
              workStatusById={workStatusById}
              filterIds={visibleIds}
              focusIds={focusIds.length ? focusIds : undefined}
              caption={farmName ? t(m.mapCaptionFarm, { work: work.name, farm: farmName }) : t(m.mapCaptionAll, { work: work.name })}
              panel={{
                title: t(m.mapTitle),
                summary: countOf(visibleIds.length, WORDS.contour),
                content: (
                  <>
                    {only.size ? <button type="button" className="astra-link" onClick={() => setOnly(new Set())}>{t(m.showAllStatuses)}</button> : null}
                    {statusFilter(true)}
                  </>
                ),
              }}
              cardExtra={c => {
                const row = rowById.get(c.id);
                return row ? (
                  <ContourWorkFacts
                    row={row}
                    names={names}
                    campaignFarms={props.campaignFarms}
                    params={params}
                    window={work.window}
                    linkSearch={props.linkSearch}
                    workHrefFor={props.workHrefFor}
                    isFarmer={props.isFarmer}
                    filtered={actorId !== null}
                  />
                ) : null;
              }}
            />
            {statusFilter(false)}
          </div>
        </Card>
      </div>

      <div className="wcamp__lower">
        <div data-guide="farms">
          <Card size="lg" title={t(m.farmsTitle)} info={t(m.farmsInfo)} aside={countOf(farms.length, WORDS.farm)}>
            <div className="wcamp__chips" role="tablist" aria-label={t(m.chipsAria)}>
              {(['all', 'overdue', 'in_window', 'in_progress', 'done', 'awaiting'] as const)
                .filter(s => s === 'all' || byFarmCount[s] > 0)
                .map(s => (
                  <button key={s} type="button" role="tab" aria-selected={farmFilter === s} className="wcamp__chip" onClick={() => setFarmFilter(s)}>
                    {s === 'all' ? t(m.chipAll) : STATUS_META[s].label}
                    <span>{num(s === 'all' ? farms.length : byFarmCount[s])}</span>
                  </button>
                ))}
            </div>
            <DataTable
              columns={farmColumns}
              rows={farmList}
              getKey={f => String(f.actorId)}
              denominator={m.farmWord}
              empty={{ title: t(m.farmsEmptyTitle), reason: t(m.farmsEmptyReason) }}
            />
          </Card>
        </div>
        <div data-guide="pace">
          {failed(daily) ? (
            <Card title={t(m.paceTitle)}><LoadError compact what={t(m.paceWhat)} error={daily.error} onRetry={daily.reload} /></Card>
          ) : !pace && daily.state !== 'loading' ? (
            <Card title={t(m.paceTitle)}><p className="t-caption">{t(m.paceNone)}</p></Card>
          ) : !pace ? (
            <WorkPaceSkeleton />
          ) : (
            <PaceChart title={work.name} heading={t(m.paceTitle)} pace={pace} total={obligations} windowTo={work.window.to} demo={props.demo} print={false} />
          )}
        </div>
      </div>
    </>
  );
}

/** Полоса «контуры хозяйства по статусу»: доля каждого статуса и подпись. */
function ContourStrip({ farm }: { farm: FarmStatusRow }) {
  const t = useT();
  const parts = STATUS_ORDER.filter(s => farm.byStatus[s] > 0);
  return (
    <span className="wcamp__strip" title={parts.map(s => t(m.stripTitle, { status: STATUS_META[s].label, count: farm.byStatus[s] })).join(' · ')}>
      <span className="wcamp__strip-bar" aria-hidden>
        {parts.map(s => <i key={s} style={{ flexGrow: farm.byStatus[s], background: `var(${STATUS_META[s].swatch})` }} />)}
      </span>
      <small>{parts.map(s => t(m.stripCount, { count: num(farm.byStatus[s]), status: STATUS_META[s].short })).join(' · ')}</small>
    </span>
  );
}

/**
 * Раздел этой работы в общей карточке контура: статус, даты окна и отчёта,
 * хозяйство со ссылками. Даты отчёта читаются по нажатию (обязательство
 * хозяйства по этой работе), а не заранее на всю карту.
 */
function ContourWorkFacts({ row, names, campaignFarms, params, window, linkSearch, workHrefFor, isFarmer, filtered }: {
  row: ContourStatusRow;
  names: ReadonlyMap<number, string>;
  campaignFarms: readonly CampaignFarm[];
  params: QueryParams;
  window: { from: string; to: string };
  linkSearch: string;
  workHrefFor: (farmId: string | null) => string;
  isFarmer: boolean;
  filtered: boolean;
}) {
  const t = useT();
  const status = fromServer(row.status);
  const farmObligation = campaignFarms.find(f => row.actor_ids.includes(f.actor.id))?.farm_obligation_id ?? null;
  const detail = useRequest(
    () => (farmObligation ? api.farmObligation(farmObligation, params) : Promise.resolve(null)),
    [farmObligation, JSON.stringify(params)],
  );
  const lines = (detail.data?.contours ?? []).filter(c => c.contour_id === row.contour_id);
  const reportedOn = lines.map(l => l.reported_on).filter((d): d is string => !!d).sort().at(-1) ?? null;
  const progress = lines.some(l => l.progress === 'in_progress') ? t(m.cardReportInProgress) : lines.some(l => l.progress === 'done') ? t(m.cardReportDone) : null;
  const by = row.by_status;
  return (
    <dl className="wcamp__card">
      <dt>{t(m.cardStatus)}</dt>
      <dd>
        {status ? <span className="wcamp__status"><i style={{ background: `var(${STATUS_META[status].swatch})` }} aria-hidden />{STATUS_META[status].label}</span> : t(m.cardNotNamed)}
        {row.obligations > 1 ? <small> · {t(m.cardMulti, { count: row.obligations, done: num(by.reported), progress: num(by.in_progress ?? 0), silent: num(by.in_window + by.overdue + by.awaiting_window) })}</small> : null}
      </dd>
      <dt>{t(m.cardWindow)}</dt>
      <dd>{row.days_overdue
        ? t(m.cardWindowAgo, { from: dateShort(window.from), to: dateShort(window.to), days: row.days_overdue })
        : row.next_deadline
          ? t(m.cardWindowUntil, { from: dateShort(window.from), to: dateShort(window.to), date: dateShort(row.next_deadline) })
          : t(m.cardWindowPlain, { from: dateShort(window.from), to: dateShort(window.to) })}</dd>
      <dt>{t(m.cardReport)}</dt>
      <dd>
        {detail.state === 'loading' ? <Skeleton width={140} />
          : failed(detail) ? <button type="button" className="astra-link" onClick={detail.reload}>{t(m.cardReportFailed)}</button>
          : reportedOn
            ? (lines[0]?.reported_by
              ? t(m.cardReportLineBy, { progress: progress ?? t(m.cardReportPlain), date: dateShort(reportedOn), by: lines[0].reported_by })
              : t(m.cardReportLine, { progress: progress ?? t(m.cardReportPlain), date: dateShort(reportedOn) }))
          : t(m.cardNoReport)}
      </dd>
      <dt>{row.actor_ids.length > 1 ? t(m.cardFarms) : t(m.cardFarm)}</dt>
      <dd className="wcamp__card-farms">
        {row.actor_ids.map(id => (
          <span key={id}>
            {isFarmer ? names.get(id) ?? t(m.farmNo, { id: String(id) }) : <Link to={`/farms/${id}${linkSearch}`}>{names.get(id) ?? t(m.farmNo, { id: String(id) })}</Link>}
            {!isFarmer && !filtered ? <> · <Link to={workHrefFor(String(id))}>{t(m.cardThisWork)}</Link></> : null}
          </span>
        ))}
      </dd>
    </dl>
  );
}
